import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, computed, effect, inject, input, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { InstitutionActivityProfessional, InstitutionActivityResponse } from '../../../api/app-types/institution.app';
import { INSTITUTION_SERVICE } from '../../../api/services/institution.service';
import { ERROR_PROVIDER } from '../../../core/services/error.service';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';

type ActivityStatus = 'loading' | 'loaded' | 'not-found' | 'unauthorized' | 'error';

interface ActivityScope {
	institutionId: number;
	branchId?: number;
}

@Component({
	selector: 'lvr-institution-activity',
	templateUrl: './institution-activity.html',
	styleUrl: './institution-activity.scss',
	imports: [MatButtonModule, MatProgressSpinnerModule, MatTableModule, RouterLink, TranslocoPipe],
})
export class InstitutionActivity {
	public readonly institutionId = input.required<number>();
	public readonly branchId = input<number>();
	private readonly institutionService = inject(INSTITUTION_SERVICE);
	private readonly errorService = inject(ERROR_PROVIDER);
	private readonly session = inject(SessionManagerService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly scope = computed(() => this.resolveScope(this.institutionId(), this.branchId()));
	private requestSequence = 0;

	public readonly status = signal<ActivityStatus>('loading');
	public readonly activity = signal<InstitutionActivityResponse | null>(null);
	public readonly displayedColumns = ['professional', 'ifd'];
	private readonly canReadUsers = hasPermission(this.session.permissions, 'Users.Read');
	private readonly canReadIfd = hasPermission(this.session.permissions, ['Ifd.All', 'Ifd.Own']);
	private readonly canReadInstitutions = hasPermission(this.session.permissions, 'Institutions.Read');
	public readonly canReadActivity = computed(() => this.canReadInstitutions() && this.canReadIfd());

	public constructor() {
		effect(() => void this.load(this.scope(), this.canReadActivity()));
		this.destroyRef.onDestroy(() => {
			this.requestSequence += 1;
		});
	}

	public professionalName(professional: InstitutionActivityProfessional): string {
		return [professional.lastName?.trim(), professional.firstName?.trim()].filter(Boolean).join(' ') || '-';
	}

	public integrationServiceOfficeName(professional: InstitutionActivityProfessional): string {
		return professional.integrationServiceOfficeName?.trim() || '-';
	}

	public canOpenUser(userId: string): boolean {
		return (
			typeof userId === 'string' &&
			/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userId) &&
			this.canReadUsers()
		);
	}

	public canOpenIfd(ifdId: number): boolean {
		return Number.isSafeInteger(ifdId) && ifdId > 0 && this.canReadIfd();
	}

	public retry(): void {
		void this.load(this.scope(), this.canReadActivity());
	}

	private resolveScope(institutionId: number, branchId?: number): ActivityScope | null {
		if (!Number.isSafeInteger(institutionId) || institutionId <= 0) {
			return null;
		}

		if (branchId === undefined) {
			return { institutionId };
		}

		if (!Number.isSafeInteger(branchId) || branchId <= 0) {
			return null;
		}

		return { institutionId, branchId };
	}

	private async load(scope: ActivityScope | null, canReadActivity: boolean): Promise<void> {
		const sequence = ++this.requestSequence;
		this.activity.set(null);
		if (!canReadActivity) {
			this.status.set('unauthorized');
			return;
		}
		this.status.set(scope ? 'loading' : 'not-found');
		if (!scope) {
			return;
		}

		try {
			const response =
				scope.branchId === undefined
					? await this.institutionService.getInstitutionActivity(scope.institutionId)
					: await this.institutionService.getBranchActivity(scope.institutionId, scope.branchId);

			if (sequence !== this.requestSequence) {
				return;
			}

			this.activity.set(response);
			this.status.set('loaded');
		} catch (error: unknown) {
			if (sequence !== this.requestSequence) {
				return;
			}

			if (error instanceof HttpErrorResponse && error.status === 404) {
				this.status.set('not-found');
				return;
			}

			await this.errorService.handleError(error, false);
			if (sequence === this.requestSequence) {
				this.status.set(
					error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)
						? 'unauthorized'
						: 'error',
				);
			}
		}
	}
}
